/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react/prop-types */
import { Col, FloatingLabel, Form, Nav, ProgressBar, Row, Tab } from 'react-bootstrap';
import FeatherIcon from 'feather-icons-react';
import document from '../../assets/img/document.png';
import trash from '../../assets/img/delete.png';
import classNames from 'classnames';
import { FileUpload } from 'primereact/fileupload';
import 'primereact/resources/themes/lara-light-cyan/theme.css';
import { useDispatch, useSelector } from 'react-redux';
import { ProjectCreationSelectors } from '../../pages/project-management/create-project/data-access/+state/project-create.selectors';
import type { Domain, FileUploadInput, Project, UploadType } from '@shared/types/project.type';
import {
  uploadFile,
  uploadAudioDeepfake,
  AUDIO_DEEPFAKE_UPLOAD_TYPE
} from '../../pages/project-management/create-project/data-access/services/fileUpload.service';
import { useMemo, useRef, useState } from 'react';
import { Tag } from 'primereact/tag';
import { setToast } from '../Toasts/data-access/toast-reducer';
import { getAccountDomain, resolveUploadDomain } from '@app/shared/utils/accountDomain.utils';
export type InventoryUploadMode = 'full' | 'tsc' | 'policy-only';

const MODE_EVENT_KEYS: Record<InventoryUploadMode, string[]> = {
  full: ['dataset', 'model', 'llm', 'privacy', 'audio', 'video', 'logs'],
  tsc: ['dataset', 'model', 'llm', 'privacy'],
  'policy-only': ['privacy']
};

export interface InventoryTabProps {
  /** Render only the Audio upload panel (no left tab nav). */
  audioOnly?: boolean;
  /** When set, upload handlers use this project (e.g. MTC+ details) even if Redux has no active project. */
  projectForUpload?: { projectId: string; domain?: Domain };
  /** `tsc` hides Audio, Video, and Logs (TSC+ uploads). `policy-only` shows policy documents only. Defaults to `full`. */
  uploadMode?: InventoryUploadMode;
  /** Fired after a successful POLICY_DOCUMENT upload. */
  onSuccessfulPolicyUpload?: () => void;
}

type NavItemType = {
  label: string;
  icon: string;
  eventKey: string;
};

const NavItem = ({ item, isLast }: { item: NavItemType; isLast?: boolean }) => {
  return (
    <Nav.Link
      eventKey={item.eventKey}
      className={classNames('text-center text-sm-start cursor-pointer outline-none d-sm-flex align-items-sm-center', {
        'border-bottom-sm border-end border-end-sm-0': !isLast
      })}
    >
      <FeatherIcon icon={item.icon} size={16} className="me-sm-2 nav-icons" />
      <span className="d-none d-sm-inline">{item.label}</span>
    </Nav.Link>
  );
};

const ALL_NAV_ITEMS: NavItemType[] = [
  {
    label: 'Dataset',
    eventKey: 'dataset',
    icon: 'tag'
  },
  {
    label: 'ML Model',
    icon: 'package',
    eventKey: 'model'
  },
  {
    label: 'LLM Output',
    icon: 'truck',
    eventKey: 'llm'
  },
  {
    label: 'Policy Documents',
    icon: 'globe',
    eventKey: 'privacy'
  },
  {
    label: 'Audio',
    icon: 'headphones',
    eventKey: 'audio'
  },
  {
    label: 'Video',
    icon: 'video',
    eventKey: 'video'
  },
  {
    label: 'Logs',
    icon: 'file-text',
    eventKey: 'logs'
  }
];

const InventoryTab = ({
  audioOnly = false,
  projectForUpload,
  uploadMode = 'full',
  onSuccessfulPolicyUpload
}: InventoryTabProps) => {
  const activeFromStore = useSelector(ProjectCreationSelectors.activeProject);

  const projectData = useMemo((): Project | null => {
    if (!projectForUpload?.projectId) return activeFromStore;
    const domain = (resolveUploadDomain(projectForUpload.domain ?? activeFromStore?.domain) ??
      getAccountDomain() ??
      'Banking') as Domain;
    if (!activeFromStore) {
      return {
        projectId: projectForUpload.projectId,
        domain,
        userId: localStorage.getItem('userId') ?? '',
        organizationId: localStorage.getItem('organizationId') ?? '',
        title: '',
        overview: ''
      } as Project;
    }
    return { ...activeFromStore, projectId: projectForUpload.projectId, domain };
  }, [activeFromStore, projectForUpload]);

  const allowedEventKeys = useMemo(() => new Set(MODE_EVENT_KEYS[uploadMode]), [uploadMode]);
  const visibleNavItems = useMemo(
    () => ALL_NAV_ITEMS.filter(item => allowedEventKeys.has(item.eventKey)),
    [allowedEventKeys]
  );
  const defaultActiveKey = visibleNavItems[0]?.eventKey ?? 'dataset';
  const hideSideNav = visibleNavItems.length === 1;

  const [totalSize, setTotalSize] = useState(0);
  const [progress, setProgress] = useState(0);
  const fileUploadRef = useRef<FileUpload>(null);
  const dispatch = useDispatch();

  // Separate title states for each upload type
  const [datasetTitle, setDatasetTitle] = useState('');
  const [mlModelTitle, setMlModelTitle] = useState('');
  const [llmModelTitle, setLlmModelTitle] = useState('');
  const [policyDocTitle, setPolicyDocTitle] = useState('');
  const [audioTitle, setAudioTitle] = useState('');
  const [videoTitle, setVideoTitle] = useState('');
  const [logsTitle, setLogsTitle] = useState('');

  const onTemplateSelect = (event: { files: File[] }) => {
    let _totalSize = totalSize;
    const files = event.files;

    files.forEach(file => {
      _totalSize += file.size;
    });
    setProgress(0);
    setTotalSize(_totalSize);
  };

  const onTemplateUpload = (event: { files: File[] }) => {
    let _totalSize = 0;
    setProgress(0);
    event.files.forEach(file => {
      _totalSize += file.size;
    });

    setTotalSize(_totalSize);
  };

  const onTemplateRemove = (file: any, callback: any) => {
    setTotalSize(totalSize - file.size);
    setProgress(0);
    callback();
  };

  const itemTemplate = (file: any, props: any) => {
    return (
      <div className="flex align-items-center flex-wrap">
        <div
          className="d-flex justify-content-between align-items-center flex-wrap"
          style={{
            gap: '10px'
          }}
        >
          <div className="d-flex align-items-center flex-grow-1" style={{ minWidth: '0' }}>
            <img alt={file.name} role="presentation" src={document} width={50} className="flex-shrink-0" />
            <span
              className="flex flex-column text-left ml-3 flex-grow-1"
              style={{
                maxWidth: '200px',
                wordWrap: 'break-word',
                overflowWrap: 'break-word',
                minWidth: '0'
              }}
            >
              <span style={{ wordBreak: 'break-all', lineHeight: '1.2' }}>{file.name}</span>
              <small>{new Date().toLocaleDateString()}</small>
            </span>
          </div>
          <div className="d-flex align-items-center gap-2 flex-shrink-0">
            <ProgressBar
              now={progress}
              label={progress}
              style={{ height: '15px', width: '100px', minWidth: '80px' }}
              animated={true}
            />
            <Tag value={props.formatSize} severity="warning" className="px-2 py-1" />
            <a onClick={() => onTemplateRemove(file, props.onRemove)}>
              <img alt={file.name} role="presentation" src={trash} width={20} />
            </a>
          </div>
        </div>
      </div>
    );
  };

  const onUpload = async (event: any, uploadType: UploadType, title: string) => {
    const userId = localStorage.getItem('userId');
    const uploadDomain = resolveUploadDomain(projectData?.domain);
    if (projectData?.projectId && uploadDomain && userId) {
      const file = event.files[0];
      const input: FileUploadInput = {
        file: file,
        domain: uploadDomain,
        uploadType: uploadType,
        projectId: projectData.projectId,
        userId: userId,
        targetColumn: '',
        title: title
      };

      try {
        await uploadFile(input, (progress, status) => {
          setProgress(progress); // Update the progress value
          console.log(`Progress: ${progress}% | Status: ${status}`);
        });
        if (uploadType === 'POLICY_DOCUMENT' && onSuccessfulPolicyUpload) {
          onSuccessfulPolicyUpload();
        }
        dispatch(
          setToast({
            show: true,
            title: 'File Uploaded',
            message: 'File uploaded successfully'
          })
        );
      } catch (error) {
        dispatch(
          setToast({
            show: true,
            title: 'Upload Failed',
            message: 'An error occurred during upload'
          })
        );
        console.error('Upload failed:', error);
      }
    }
  };

  const onUploadAudio = async (event: any) => {
    const userId = localStorage.getItem('userId');
    if (!projectData?.projectId || !userId) {
      dispatch(
        setToast({
          show: true,
          title: 'Upload Failed',
          message: 'Project and user are required for audio upload'
        })
      );
      return;
    }
    const file = event.files?.[0];
    if (!file) return;

    try {
      await uploadAudioDeepfake(
        {
          file,
          projectId: projectData.projectId,
          userId,
          uploadType: AUDIO_DEEPFAKE_UPLOAD_TYPE,
          title: audioTitle || undefined,
          domain: projectData.domain ?? undefined
        },
        progress => {
          setProgress(progress);
        }
      );
      dispatch(
        setToast({
          show: true,
          title: 'Audio Uploaded',
          message: 'Audio file uploaded successfully'
        })
      );
    } catch (error: any) {
      dispatch(
        setToast({
          show: true,
          title: 'Audio Upload Failed',
          message: error?.message || 'An error occurred during audio upload'
        })
      );
      console.error('Audio upload failed:', error);
    }
  };

  // Calculate and format file size
  // const formatSize = (size: number): string => {
  //   if (size < 1024) return `${size} bytes`;
  //   else if (size < 1048576) return `${(size / 1024).toFixed(2)} KB`;
  //   else if (size < 1073741824) return `${(size / 1048576).toFixed(2)} MB`;
  //   else return `${(size / 1073741824).toFixed(2)} GB`;
  // };

  // Header template for the FileUpload component
  // const headerTemplate = (options: any) => {
  //   const { className, chooseButton, uploadButton, cancelButton } = options;
  //   const value = totalSize / 10000;
  //
  //   return (
  //     <div
  //       className={className}
  //       style={{
  //         backgroundColor: 'transparent',
  //         display: 'flex',
  //         alignItems: 'center'
  //       }}
  //     >
  //       {chooseButton}
  //       {uploadButton}
  //       {cancelButton}
  //     </div>
  //   );
  // };
  // const chooseOptions = {
  //   icon: 'folder-plus',
  //   iconOnly: true,
  //   className: 'custom-choose-btn p-button-rounded p-button-outlined'
  // };
  // const uploadOptions = {
  //   icon: 'pi pi-fw pi-cloud-upload',
  //   iconOnly: true,
  //   className:
  //     'custom-upload-btn p-button-success p-button-rounded p-button-outlined'
  // };
  // const cancelOptions = {
  //   icon: 'pi pi-fw pi-times',
  //   iconOnly: true,
  //   className:
  //     'custom-cancel-btn p-button-danger p-button-rounded p-button-outlined'
  // };

  if (audioOnly) {
    return (
      <div className="d-flex flex-column h-100">
        <h5 className="mb-2 text-body-highlight">Upload audio</h5>
        <p className="text-body-tertiary small mb-3">
          Upload audio files for deep fake analysis (same pipeline as project uploads). Allowed: .wav, .mp3, .m4a,
          .flac, .ogg, .webm
        </p>
        <FloatingLabel controlId="floatingAudioTitleMtc" label="Title (optional)" className="mb-3">
          <Form.Control
            onChange={e => setAudioTitle(e.target.value)}
            type="text"
            placeholder="Title"
            value={audioTitle}
          />
        </FloatingLabel>
        <FileUpload
          ref={fileUploadRef}
          name="file_audio_mtc"
          accept=".wav,.mp3,.m4a,.flac,.ogg,.webm"
          onUpload={onTemplateUpload}
          onSelect={onTemplateSelect}
          itemTemplate={itemTemplate as any}
          emptyTemplate={<p className="m-0">Drag and drop audio files here (WAV, MP3, M4A, FLAC, OGG, WebM).</p>}
          onProgress={evt => setProgress(evt.progress)}
          customUpload
          uploadHandler={onUploadAudio}
        />
      </div>
    );
  }

  return (
    <Tab.Container id="left-tabs-example" key={uploadMode} defaultActiveKey={defaultActiveKey}>
      <Row className="g-0 border-top border-bottom">
        {!hideSideNav && (
          <Col xs={12} sm={3}>
            <Nav className="flex-sm-column border-bottom border-bottom-sm-0 border-end-sm fs-9 vertical-tab h-100 justify-content-between">
              {visibleNavItems.map((item, index) => (
                <NavItem key={item.eventKey} item={item} isLast={index === visibleNavItems.length - 1} />
              ))}
            </Nav>
          </Col>
        )}

        <Col xs={12} sm={hideSideNav ? 12 : 9}>
          <Tab.Content className="py-3 ps-sm-4 h-100">
            {allowedEventKeys.has('dataset') && (
              <Tab.Pane eventKey="dataset" className="h-100">
                <div className="d-flex flex-column h-100">
                  <h5 className="mb-3 text-body-highlight">Dataset</h5>
                  <FloatingLabel controlId="floatingDatasetTitle" label="Title" className="mb-3">
                    <Form.Control
                      onChange={e => setDatasetTitle(e.target.value)}
                      type="text"
                      placeholder="Title"
                      value={datasetTitle}
                    />
                  </FloatingLabel>
                  <FileUpload
                    ref={fileUploadRef}
                    name="file_dataset"
                    onUpload={onTemplateUpload}
                    onSelect={onTemplateSelect}
                    // headerTemplate={headerTemplate}
                    // itemTemplate={itemTemplate}

                    itemTemplate={itemTemplate as any}
                    emptyTemplate={<p className="m-0">Drag and drop files to here to upload.</p>}
                    // headerTemplate={headerTemplate}
                    // onUpload={() => setProgress(0)}
                    onProgress={evt => setProgress(evt.progress)}
                    // progressBarTemplate={<div>Uploading: {progress}</div>}
                    customUpload
                    uploadHandler={event => onUpload(event, 'DATA_SET', datasetTitle)}
                  />
                </div>
              </Tab.Pane>
            )}
            {allowedEventKeys.has('model') && (
              <Tab.Pane eventKey="model" className="h-100">
                <div className="d-flex flex-column h-100">
                  <h5 className="mb-3 text-body-highlight">ML Model</h5>
                  <FloatingLabel controlId="floatingMlModelTitle" label="Title" className="mb-3">
                    <Form.Control
                      onChange={e => setMlModelTitle(e.target.value)}
                      type="text"
                      placeholder="Title"
                      value={mlModelTitle}
                    />
                  </FloatingLabel>
                  <FileUpload
                    ref={fileUploadRef}
                    name="file_ml_model"
                    onUpload={onTemplateUpload}
                    onSelect={onTemplateSelect}
                    // headerTemplate={headerTemplate}
                    // itemTemplate={itemTemplate}

                    itemTemplate={itemTemplate as any}
                    emptyTemplate={<p className="m-0">Drag and drop files to here to upload.</p>}
                    // headerTemplate={headerTemplate}
                    // onUpload={() => setProgress(0)}
                    onProgress={evt => setProgress(evt.progress)}
                    // progressBarTemplate={<div>Uploading: {progress}</div>}
                    customUpload
                    uploadHandler={event => onUpload(event, 'ML_MODEL', mlModelTitle)}
                  />
                </div>
              </Tab.Pane>
            )}

            {allowedEventKeys.has('llm') && (
              <Tab.Pane eventKey="llm" className="h-100">
                <div className="d-flex flex-column h-100">
                  <h5 className="mb-3 text-body-highlight">LLM Model</h5>
                  <FloatingLabel controlId="floatingLlmModelTitle" label="Title" className="mb-3">
                    <Form.Control
                      onChange={e => setLlmModelTitle(e.target.value)}
                      type="text"
                      placeholder="Title"
                      value={llmModelTitle}
                    />
                  </FloatingLabel>
                  <FileUpload
                    ref={fileUploadRef}
                    name="file_dataset"
                    onUpload={onTemplateUpload}
                    onSelect={onTemplateSelect}
                    // headerTemplate={headerTemplate}
                    // itemTemplate={itemTemplate}

                    itemTemplate={itemTemplate as any}
                    emptyTemplate={<p className="m-0">Drag and drop files to here to upload.</p>}
                    // headerTemplate={headerTemplate}
                    // onUpload={() => setProgress(0)}
                    onProgress={evt => setProgress(evt.progress)}
                    // progressBarTemplate={<div>Uploading: {progress}</div>}
                    customUpload
                    uploadHandler={event => onUpload(event, 'LLM_MODEL', llmModelTitle)}
                  />
                </div>
              </Tab.Pane>
            )}

            {allowedEventKeys.has('privacy') && (
              <Tab.Pane eventKey="privacy" className="h-100">
                <div className="d-flex flex-column h-100">
                  <h5 className="mb-3 text-body-highlight">Policy documents</h5>
                  <FloatingLabel controlId="floatingPolicyDocTitle" label="Title" className="mb-3">
                    <Form.Control
                      onChange={e => setPolicyDocTitle(e.target.value)}
                      type="text"
                      placeholder="Title"
                      value={policyDocTitle}
                    />
                  </FloatingLabel>
                  <FileUpload
                    ref={fileUploadRef}
                    name="file_dataset"
                    onUpload={onTemplateUpload}
                    onSelect={onTemplateSelect}
                    // headerTemplate={headerTemplate}
                    // itemTemplate={itemTemplate}

                    itemTemplate={itemTemplate as any}
                    emptyTemplate={<p className="m-0">Drag and drop files to here to upload.</p>}
                    // headerTemplate={headerTemplate}
                    // onUpload={() => setProgress(0)}
                    onProgress={evt => setProgress(evt.progress)}
                    // progressBarTemplate={<div>Uploading: {progress}</div>}
                    customUpload
                    uploadHandler={event => onUpload(event, 'POLICY_DOCUMENT', policyDocTitle)}
                  />
                </div>
              </Tab.Pane>
            )}

            {allowedEventKeys.has('audio') && (
              <Tab.Pane eventKey="audio" className="h-100">
                <div className="d-flex flex-column h-100">
                  <h5 className="mb-3 text-body-highlight">Audio</h5>
                  <p className="text-body-tertiary small mb-2">Allowed: .wav, .mp3, .m4a, .flac, .ogg, .webm</p>
                  <FloatingLabel controlId="floatingAudioTitle" label="Title (optional)" className="mb-3">
                    <Form.Control
                      onChange={e => setAudioTitle(e.target.value)}
                      type="text"
                      placeholder="Title"
                      value={audioTitle}
                    />
                  </FloatingLabel>
                  <FileUpload
                    ref={fileUploadRef}
                    name="file_audio"
                    accept=".wav,.mp3,.m4a,.flac,.ogg,.webm"
                    onUpload={onTemplateUpload}
                    onSelect={onTemplateSelect}
                    itemTemplate={itemTemplate as any}
                    emptyTemplate={
                      <p className="m-0">Drag and drop audio files here (WAV, MP3, M4A, FLAC, OGG, WebM).</p>
                    }
                    onProgress={evt => setProgress(evt.progress)}
                    customUpload
                    uploadHandler={onUploadAudio}
                  />
                </div>
              </Tab.Pane>
            )}

            {allowedEventKeys.has('video') && (
              <Tab.Pane eventKey="video" className="h-100">
                <div className="d-flex flex-column h-100">
                  <h5 className="mb-3 text-body-highlight">Video</h5>
                  <FloatingLabel controlId="floatingVideoTitle" label="Title" className="mb-3">
                    <Form.Control
                      onChange={e => setVideoTitle(e.target.value)}
                      type="text"
                      placeholder="Title"
                      value={videoTitle}
                      disabled
                    />
                  </FloatingLabel>
                  <FileUpload
                    ref={fileUploadRef}
                    name="file_dataset"
                    onUpload={onTemplateUpload}
                    disabled
                    onSelect={onTemplateSelect}
                    // headerTemplate={headerTemplate}
                    // itemTemplate={itemTemplate}

                    itemTemplate={itemTemplate as any}
                    emptyTemplate={<p className="m-0">Drag and drop video files to here to upload.</p>}
                    // headerTemplate={headerTemplate}
                    // onUpload={() => setProgress(0)}
                    onProgress={evt => setProgress(evt.progress)}
                    // progressBarTemplate={<div>Uploading: {progress}</div>}
                    customUpload
                    uploadHandler={event => onUpload(event, 'VIDEO', videoTitle)}
                  />
                </div>
              </Tab.Pane>
            )}

            {allowedEventKeys.has('logs') && (
              <Tab.Pane eventKey="logs" className="h-100">
                <div className="d-flex flex-column h-100">
                  <h5 className="mb-3 text-body-highlight">Logs</h5>
                  <FloatingLabel controlId="floatingLogsTitle" label="Title" className="mb-3">
                    <Form.Control
                      onChange={e => setLogsTitle(e.target.value)}
                      type="text"
                      placeholder="Title"
                      value={logsTitle}
                      disabled
                    />
                  </FloatingLabel>
                  <FileUpload
                    ref={fileUploadRef}
                    name="file_dataset"
                    onUpload={onTemplateUpload}
                    disabled
                    onSelect={onTemplateSelect}
                    // headerTemplate={headerTemplate}
                    // itemTemplate={itemTemplate}

                    itemTemplate={itemTemplate as any}
                    emptyTemplate={<p className="m-0">Drag and drop logs files to here to upload.</p>}
                    // headerTemplate={headerTemplate}
                    // onUpload={() => setProgress(0)}
                    onProgress={evt => setProgress(evt.progress)}
                    // progressBarTemplate={<div>Uploading: {progress}</div>}
                    customUpload
                    uploadHandler={event => onUpload(event, 'LOGS', logsTitle)}
                  />
                </div>
              </Tab.Pane>
            )}
          </Tab.Content>
        </Col>
      </Row>
    </Tab.Container>
  );
};

export default InventoryTab;
